import { Marker, MarkerProps } from "react-leaflet";
import { Pose } from "types/geometry";

import * as L from "leaflet";
import { point2DToLatLng } from "lib/utils/geometry";
import { useMemo } from "react";
import { Point2D } from "arcjs/common/geometry";

type PoseMarkerProps = Omit<MarkerProps, "position"> & {
    pose: Pose;
};

const workaround_fixMarkerInteractivity = (
    marker: L.Marker | null,
    interactive: boolean,
) => {
    // sometimes marker still doesn't let clicks pass through when interactive is set to `false`. This is the css workaround to tell browser not to pass any click event to the marker
    const elem = marker?.getElement();
    if (!elem) return;
    elem.style.pointerEvents = interactive ? "auto" : "none";
};

export const PoseMarker = ({ pose, interactive = true, ...others }: PoseMarkerProps) => {
    return (
        <Marker
            ref={(marker) => {
                workaround_fixMarkerInteractivity(marker, interactive);
            }}
            {...others}
            position={point2DToLatLng(pose.position)}
        />
    );
};

interface PointMarkerProps extends Omit<MarkerProps, "position"> {
    point: Point2D;
    svgPath: string;
    iconSize?: L.PointTuple;
    iconAnchor?: L.PointTuple;
    markerClassName?: string;
}

const POSE_MARKER_CLASS_NAME = `min-w-[24px] aspect-square max-w-[24px]`;
const POSE_MARKER_ICON_SIZE: L.PointTuple = [24, 24];
const MARKER_IMAGE_CLASS_NAME = "w-full h-full object-contain";
const MARKER_DIV_ICON_CLASS_NAME = "border-0";

const LOCATION_CIRCLE_SVG = require("assets/svgs/navigation/map/location_circle.svg").default;
const LOCATION_CIRCLE_ICON_SIZE: L.PointTuple = [24, 24];
const LOCATION_CIRCLE_ICON_ANCHOR: L.PointTuple = [12, 12];

/** Shared robot / navigate-pose glyph — explicit anchor + centered rotation. */
const makeLocationCircleDivIcon = (opts: {
    yawDegrees?: number;
    title?: string;
    /** Serial label below-right of the icon (fleet robot marker). */
    robotTitle?: boolean;
}): L.DivIcon => {
    const { yawDegrees, title, robotTitle } = opts;
    let wrapperClass = POSE_MARKER_CLASS_NAME;
    if (title && robotTitle) {
        wrapperClass +=
            " block relative before:content-[attr(title)] before:pointer-events-none before:block before:absolute before:top-[100%] before:left-[100%] before:bg-[#a4a4a4] before:p-1 before:whitespace-nowrap before:text-xs before:rounded";
    }
    const rotationStyle =
        yawDegrees !== undefined
            ? `style="display:block;transform-origin:center center;transform:rotate(${yawDegrees}deg);"`
            : `style="display:block"`;
    const safeTitle = title ? title.replace(/"/g, "&quot;") : "";
    const titleAttr = safeTitle ? ` title="${safeTitle}"` : "";
    const html =
        `<div${titleAttr} class="${wrapperClass}">` +
        `<img src=${LOCATION_CIRCLE_SVG} class="${MARKER_IMAGE_CLASS_NAME}" draggable="false" ${rotationStyle}>` +
        `</div>`;
    return new L.DivIcon({
        html,
        className: MARKER_DIV_ICON_CLASS_NAME,
        iconSize: LOCATION_CIRCLE_ICON_SIZE,
        iconAnchor: LOCATION_CIRCLE_ICON_ANCHOR,
    });
};

export const PointMarker = ({
    point,
    svgPath = require("assets/svgs/navigation/map/Location.svg").default,
    iconSize = [36, 36],
    iconAnchor = [18, 36],
    markerClassName = `min-w-[36px] aspect-square max-w-[36px]`,
    ...others
}: PointMarkerProps) => {
    const icon = useMemo(() => {
        return new L.DivIcon({
            html: `<div class="${markerClassName}"><img src=${svgPath} class="${MARKER_IMAGE_CLASS_NAME}" draggable="false"></div>`,
            className: MARKER_DIV_ICON_CLASS_NAME,
            iconSize: iconSize,
            iconAnchor: iconAnchor,
        });
    }, [markerClassName, svgPath, iconSize, iconAnchor]);
    return <Marker {...others} position={point2DToLatLng(point)} icon={icon} />;
};

export const SelectedPointMarker = ({ point, ...others }: Omit<PointMarkerProps, "svgPath">) => {
    return (
        <PointMarker
            {...others}
            point={point}
            svgPath={require("assets/svgs/navigation/map/SelectedLocation.svg").default}
        />
    );
};

const WAYPOINT_PIN_SVG = require("assets/svgs/navigation/map/Location.svg").default;

/** Per-actionType icon for non-navigation mission markers (photo/wait/stair/dock). */
export const MISSION_ACTION_MARKER_SVGS: Record<string, string> = {
    TakePhoto: require("assets/svgs/Image.svg").default,
    Record360: require("assets/svgs/Image.svg").default,
    Wait: require("assets/svgs/Clock.svg").default,
    Docking: require("assets/svgs/docking.svg").default,
    Dock: require("assets/svgs/docking.svg").default,
    Undock: require("assets/svgs/docking.svg").default,
};

// Stair glyph drawn inline: the Stair.svg asset is a 200mm Illustrator export
// whose shape turns into a blob when shrunk into a small map badge.
const STAIR_GLYPH_SVG =
    '<svg viewBox="0 0 24 24" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M2 22 V17 H7 V12 H12 V7 H17 V2 H22 V22 Z" fill="#ffffff"/>' +
    "</svg>";

const MISSION_ACTION_MARKER_SIZE = 20;
/** Rightward trail past the anchor waypoint pin (pin half-width + gap per slot). */
const missionActionMarkerOffsetX = (stackIndex: number, wide = false): number =>
    stackIndex * ((wide ? 24 : MISSION_ACTION_MARKER_SIZE) + 4) + 4;

/**
 * Non-navigation mission item chip: dark circular badge with the action's icon.
 * These actions store no pose, so they anchor at their waypoint and stack to the
 * right. Display-only (non-interactive).
 */
export const MissionActionMarker = ({
    point,
    actionType,
    stackIndex = 0,
    ...others
}: Omit<MarkerProps, "position"> & {
    point: Point2D;
    actionType: string;
    stackIndex?: number;
}) => {
    const svg = MISSION_ACTION_MARKER_SVGS[actionType];
    const rotation = actionType === "Undock" ? "rotate(180deg)" : "";
    // WalkStair uses the black rounded-square tile look (like its action button);
    // other items stay circular chips.
    const isTile = actionType === "WalkStair";
    const size = isTile ? 24 : MISSION_ACTION_MARKER_SIZE;
    const icon = useMemo(() => {
        const offsetX = missionActionMarkerOffsetX(stackIndex, isTile);
        const glyph = isTile
            ? // Inline stair glyph fills the tile directly (no <img> filter needed).
              STAIR_GLYPH_SVG
            : // brightness(0) invert(1) normalizes currentColor/white glyphs to white,
              // since SVGs loaded via <img> cannot inherit CSS color.
              `<img src=${svg} style="width:65%;height:65%;object-fit:contain;${rotation ? `transform:${rotation};` : ""}` +
              `filter:brightness(0) invert(1);pointer-events:none" draggable="false">`;
        const html =
            `<div style="width:${size}px;height:${size}px;transform:translateX(${offsetX}px)">` +
            `<div style="width:${size}px;height:${size}px;border-radius:${isTile ? "6px" : "9999px"};background:#1a1a1a;` +
            `border:${isTile ? "none" : "1px solid rgba(255,255,255,0.85)"};display:flex;align-items:center;justify-content:center;">` +
            glyph +
            `</div></div>`;
        return new L.DivIcon({
            html,
            className: MARKER_DIV_ICON_CLASS_NAME,
            iconSize: [size, size],
            iconAnchor: [size / 2, size / 2],
        });
    }, [svg, rotation, stackIndex, size, isTile]);

    return <Marker {...others} position={point2DToLatLng(point)} icon={icon} />;
};

const WAYPOINT_MARKER_SIZES = {
    compact: { size: 22, anchorY: 22, badge: 13, font: 9 },
    default: { size: 30, anchorY: 30, badge: 14, font: 10 },
} as const;

interface WaypointMarkerProps extends Omit<MarkerProps, "position"> {
    point: Point2D;
    /** Zero-based index; displayed as index + 1 on the pin. */
    index: number;
    /** "compact" for inset maps, "default" for the full map view. */
    size?: keyof typeof WAYPOINT_MARKER_SIZES;
}

export const WaypointMarker = ({
    point,
    index,
    size = "default",
    ...others
}: WaypointMarkerProps) => {
    const { size: px, anchorY, badge: badgePx, font: fontPx } = WAYPOINT_MARKER_SIZES[size];
    const half = px / 2;
    const label = index + 1;

    const icon = useMemo(() => {
        const badgeStyle =
            "position:absolute;left:100%;top:100%;transform:translate(-35%,-35%);" +
            `min-width:${badgePx}px;height:${badgePx}px;padding:0 2px;display:flex;align-items:center;justify-content:center;` +
            "border-radius:9999px;background:#1a1a1a;color:#fff;" +
            `font-size:${fontPx}px;font-weight:700;line-height:1;` +
            "border:1px solid rgba(255,255,255,0.85);pointer-events:none;";
        return new L.DivIcon({
            html:
                `<div style="position:relative;width:${px}px;height:${px}px">` +
                `<img src=${WAYPOINT_PIN_SVG} class="${MARKER_IMAGE_CLASS_NAME}" draggable="false">` +
                `<span style="${badgeStyle}">${label}</span>` +
                `</div>`,
            className: MARKER_DIV_ICON_CLASS_NAME,
            iconSize: [px, px],
            iconAnchor: [half, anchorY],
        });
    }, [label, px, half, anchorY, badgePx, fontPx]);

    return <Marker {...others} position={point2DToLatLng(point)} icon={icon} />;
};

export const NavigateToPosePointMarker = ({
    point,
    ...others
}: Omit<PointMarkerProps, "svgPath">) => {
    const icon = useMemo(() => makeLocationCircleDivIcon({}), []);
    return <Marker {...others} position={point2DToLatLng(point)} icon={icon} />;
};

export const NavigateToPoseHeadingMarker = ({
    point,
    theta,
    ...others
}: Omit<PointMarkerProps, "svgPath"> & { theta: number }) => {
    const yawDegrees = -theta * (180 / Math.PI) - 90;
    const icon = useMemo(() => makeLocationCircleDivIcon({ yawDegrees }), [yawDegrees]);
    return (
        <Marker
            {...others}
            key={`navigate-heading-${Math.round(yawDegrees)}`}
            position={point2DToLatLng(point)}
            icon={icon}
        />
    );
};

const makeTitledIcon = (title: string, uri: string, rotation?: number) => {
    const labelClassNames = title
        ? `block relative before:content-[attr(title)] before:block before:absolute before:top-[-100%] before:bg-[#a4a4a4] before:p-1`
        : "";
    const className = POSE_MARKER_CLASS_NAME;
    const imageClassNames = MARKER_IMAGE_CLASS_NAME;
    const rotationStyle = rotation !== undefined ? `style="transform: rotate(${rotation}deg);"` : "";
    return new L.DivIcon({
        html: `<div title=${title} class="${className} ${labelClassNames}"><img src=${uri} class="${imageClassNames}" draggable="false" ${rotationStyle}></div>`,

        className: MARKER_DIV_ICON_CLASS_NAME,
        iconSize: POSE_MARKER_ICON_SIZE,
    });
};

export const LandmarkMarker = ({
    title = "Unknown Landmark",
    pose,
    ...others
}: PoseMarkerProps) => {
    const icon = useMemo(() => {
        return makeTitledIcon(title, require("assets/svgs/navigation/map/Landmark.svg").default);
    }, [title]);
    return <PoseMarker {...others} riseOnHover={true} title={title} pose={pose} icon={icon} />;
};

export const RobotMarker = ({ title = "Unknown Robot", pose, ...others }: PoseMarkerProps) => {
    const icon = useMemo(() => {
        // Extract yaw angle from quaternion for 2D rotation
        // For quaternion (x, y, z, w), yaw around Z-axis is:
        // yaw = atan2(2*(w*z + x*y), 1 - 2*(y*y + z*z))
        const { x, y, z, w } = pose.orientation;
        const yawRadians = Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z));
        // Convert to degrees, negate for CSS rotation direction, subtract 90° offset (icon points north at 0°, ROS uses east)
        const yawDegrees = -yawRadians * (180 / Math.PI) - 90;

        return makeLocationCircleDivIcon({
            yawDegrees,
            title,
            robotTitle: Boolean(title),
        });
    }, [title, pose.orientation]);

    return <PoseMarker {...others} pose={pose} icon={icon} />;
};
